import { Link } from "react-router-dom";
import {
  ArrowUpRight, Check,
  TrendingUp, Rocket, Database, Sparkles, LineChart, Users, ShieldCheck, Building2, Handshake,
  Award, Scale, Layers, Target, Compass,
} from "lucide-react";
import Seo from "@/components/common/Seo";
import { Reveal, KineticHeading, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import StrategyNetwork from "@/components/heroes/FractionalCIONetwork";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const WHEN = [
  { icon: TrendingUp, title: "Rapid business growth", body: "Ambition is outpacing the systems and leadership meant to support it." },
  { icon: Building2, title: "Business expansion", body: "New markets, sites or brands require a stronger technology footing to scale on." },
  { icon: Layers, title: "Technology becoming more complex", body: "The organisation is running more platforms, partners and data than any one leader can hold together." },
  { icon: Database, title: "ERP modernisation", body: "The core platform decision is too large, and too costly, to get wrong." },
  { icon: Sparkles, title: "AI adoption", body: "Leadership needs a clear, commercial position on where AI actually pays." },
  { icon: ShieldCheck, title: "Cybersecurity and governance", body: "Technology and cyber risk now sit on the board's agenda and need a senior owner." },
  { icon: LineChart, title: "Technology investments without clear ROI", body: "Spend keeps rising while the link to business return stays unclear." },
  { icon: Users, title: "Leadership gap", body: "No single senior voice owns technology at board level." },
  { icon: Rocket, title: "Business transformation", body: "A significant change is on the table and needs a senior, accountable owner." },
];

const DELIVERS = [
  { title: "Align technology with business strategy", note: "Every technology move mapped to a clear commercial objective the board can defend." },
  { title: "Improve executive decision-making", note: "The clarity to make major technology decisions with confidence, at pace." },
  { title: "Develop a practical technology roadmap", note: "A prioritised, board-ready plan the business can genuinely execute." },
  { title: "Reduce technology-related business risk", note: "Delivery, cyber, vendor and continuity risk brought under control." },
  { title: "Improve governance", note: "Oversight and decision rights that keep technology accountable to leadership." },
  { title: "Guide technology investments", note: "Spend directed to where it earns the strongest, most defensible business return." },
  { title: "Support business scalability", note: "A technology core that carries the next stage of growth without breaking." },
  { title: "Strengthen leadership confidence", note: "A senior, independent voice the executive team can rely on when it matters." },
];

const WHY = [
  { icon: Award, title: "Practical experience", body: "More than 27 years of executive leadership on real programmes with real commercial consequence, not theory." },
  { icon: Users, title: "Access to executive-level leadership", body: "Board-level technology judgement in the room when the decision matters, without a permanent appointment." },
  { icon: Layers, title: "Flexible engagement model", body: "A cadence that flexes with the business, from a few days a month to a more intensive presence through a critical programme." },
  { icon: Target, title: "Business-first perspective", body: "Every recommendation begins with the business result it is meant to create. Technology follows the strategy, never the other way around." },
  { icon: Scale, title: "Independent advice", body: "No products to sell and no platforms to push. The only interest is the decision that is right for the business." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of partners, platforms and providers, free of commercial bias or hidden incentives." },
  { icon: LineChart, title: "No full-time executive cost", body: "Senior technology leadership at a fraction of the cost, commitment and overhead of a full-time appointment." },
  { icon: Compass, title: "Long-term strategic partnership", body: "A trusted advisor who stays through delivery, accountable to the outcome, not just the advice." },
];

const PROCESS = [
  { title: "Business discovery", body: "Start with the strategy, the market and the outcomes the leadership team is accountable for." },
  { title: "Executive assessment", body: "A candid read on current technology capability, cost and business risk." },
  { title: "Technology and business alignment", body: "Bring the technology direction into line with the plan for the business." },
  { title: "Strategic roadmap", body: "A prioritised, board-ready plan mapped to commercial goals and sequencing." },
  { title: "Leadership guidance", body: "Support the executive team through the two or three decisions that shape the next stage." },
  { title: "Execution oversight", body: "Hold delivery, teams and vendors accountable to the business outcome, not the activity." },
  { title: "Continuous executive advisory", body: "An ongoing, senior voice as the business and its priorities evolve." },
];

const OUTCOMES = [
  { title: "Business Growth", note: "Technology that opens new revenue, markets and capacity." },
  { title: "Operational Excellence", note: "A leaner, faster and more resilient operating core." },
  { title: "Investment Optimisation", note: "Technology spend aligned to a defensible business return." },
  { title: "Executive Visibility", note: "A clear, board-level view of value, cost and risk." },
  { title: "Risk Reduction", note: "Cyber, delivery and vendor risk brought under control." },
  { title: "Better Decision Making", note: "The clarity to decide with confidence, and at pace." },
  { title: "Scalable Operations", note: "Systems built to carry the next stage of growth." },
  { title: "Competitive Advantage", note: "Capability that competitors cannot easily copy." },
];

const BENEFITS = [
  "Growing manufacturing businesses", "Engineering companies", "Industrial businesses", "Retail and distribution", "Healthcare organisations",
  "Logistics companies", "Infrastructure businesses", "Family-owned businesses",
  "Expansion-focused organisations", "Companies entering new markets",
];

const FAQS = [
  { q: "Why engage a Fractional CIO instead of hiring a full-time CIO?", a: "A full-time CIO carries a permanent executive salary, a lengthy recruitment cycle and a fixed commitment, whether or not the business needs that level of leadership every day. A Fractional CIO provides the same board-level judgement on the decisions that actually matter, at the cadence your business needs, with the flexibility to scale up or down as priorities shift." },
  { q: "How does a Fractional CIO work with an existing IT team?", a: "The role is to lead the team, not replace it. That means setting direction, raising the standard of delivery, holding vendors to account and giving your people a senior leader to work alongside. In most engagements the internal team becomes stronger and more confident within the first few months." },
  { q: "Can a Fractional CIO support ERP modernisation?", a: "Yes. Core platform decisions are among the largest and riskiest investments a business will ever make. A Fractional CIO provides independent leadership on the choice of platform, the selection of the implementation partner and the governance of the programme, protecting both the investment and the business outcomes it is meant to create." },
  { q: "Can a Fractional CIO help develop an AI strategy?", a: "Yes. The priority is to separate genuine, commercial AI value from noise, build a focused portfolio the business can defend, and put the data, governance and guardrails in place to adopt it responsibly and scale with confidence." },
  { q: "Can this model support business expansion?", a: "Yes. Expansion into new markets, geographies or sites places real pressure on the operating core. A Fractional CIO helps leadership plan the technology capacity, governance and integration required to scale cleanly, without slowing the pace of the business." },
  { q: "How are engagements structured?", a: "Engagements are shaped around your business. They typically begin with a focused executive assessment, followed by a defined advisory cadence, from a few days a month to a more intensive presence during a critical programme. The commitment flexes up and down as the business requires." },
];

const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Fractional CIO",
  serviceType: "Fractional CIO & Virtual CIO Services",
  description: "Executive technology leadership for growing businesses, mid-market companies and family-owned enterprises, without the cost of a full-time CIO. Advisory across India, the UAE and the Gulf Region.",
  provider: { "@type": "Person", name: "Aftab Azad" },
  areaServed: ["India", "United Arab Emirates", "Gulf Region", "Middle East", "Singapore", "Global"],
  url: "https://aftabazad.com/services/fractional-cio",
};

export default function FractionalCIO() {
  return (
    <>
      <Seo
        title="Fractional CIO & Virtual CIO Services"
        description="Executive technology leadership without the cost of a full-time CIO. Aftab Azad works alongside business owners, CEOs and executive teams in growing businesses, family-owned enterprises and mid-market companies across India, the UAE and the Gulf Region."
        path="/services/fractional-cio"
        keywords={["Fractional CIO", "Fractional CIO India", "Fractional CIO UAE", "Fractional CIO Gulf", "Virtual CIO", "Mid-Market CIO", "Family-Owned Business Advisor", "Enterprise Technology Leadership", "Executive Technology Advisor"]}
        jsonLd={serviceLd}
      />

      {/* SECTION 1 — HERO */}
      <section data-testid="fcio-hero" className="relative overflow-hidden bg-navy pb-24 pt-40 text-white grain-overlay md:pb-28 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[600px] w-[600px] rounded-full bg-royal/20 blur-[140px]" />
        <StrategyNetwork className="pointer-events-none absolute inset-y-10 right-0 hidden h-[calc(100%-5rem)] w-[38%] max-w-[520px] pr-4 opacity-90 md:block lg:w-[40%]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.3em] text-sky">Fractional CIO</p>
          <h1 className="mt-6 max-w-5xl font-heading text-4xl font-light leading-[1.04] tracking-tight md:text-[3.4rem]">
            <KineticHeading lines={["Executive technology leadership,", "without the cost of"]} lineClassName="font-light" />
            <KineticHeading lines={["a full-time CIO."]} lineClassName="font-black text-sky" />
          </h1>
          <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-slate-300 md:text-xl">
            As businesses grow, technology decisions become increasingly important to expansion,
            operational efficiency and long-term competitiveness. Many organisations need experienced
            technology leadership but do not require a full-time Chief Information Officer. Aftab Azad
            works alongside business owners, CEOs, managing directors and executive teams as a
            Fractional CIO, helping them make better technology decisions, strengthen governance,
            modernise operations and build a practical technology roadmap that supports sustainable
            business growth across India, the UAE and the Gulf Region.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/book" data-testid="fcio-hero-book"
              className="group inline-flex items-center gap-2 rounded-full bg-royal px-8 py-4 font-body text-base font-semibold text-white transition-colors duration-300 hover:bg-sky hover:text-navy">
              Book Executive Strategy Session
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/services/executive-advisory" data-testid="fcio-hero-guide"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-body text-base font-medium text-white transition-colors hover:border-sky hover:text-sky">
              Explore Advisory Services
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/10 pt-6">
            {["27+ years executive leadership", "India", "UAE", "Gulf Region", "Global"].map((t, i, arr) => (
              <span key={t} className="flex items-center gap-3">
                <span className="font-body text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">{t}</span>
                {i < arr.length - 1 && <span className="h-1 w-1 rounded-full bg-sky/50" />}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — WHEN TO CONSIDER */}
      <section data-testid="fcio-when" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">When to consider one</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["When does a business", "need a Fractional CIO?"]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {WHEN.map((w, i) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} delay={(i % 3) * 0.06}>
                  <div className="flex h-full flex-col rounded-[1.5rem] border border-slate-200 bg-white p-8">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-6 font-heading text-xl font-bold leading-snug tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-3 font-body leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3 — WHAT IT DELIVERS */}
      <section data-testid="fcio-delivers" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">How value is created</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Business outcomes,", "not a job description."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {DELIVERS.map((d) => (
              <Reveal key={d.title} className="flex items-start gap-4 border-t border-slate-200 pt-6">
                <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-navy text-white">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <div>
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{d.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{d.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHY AFTAB */}
      <section data-testid="fcio-why" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why growing businesses choose a Fractional CIO</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-10 md:mt-12 grid gap-x-12 gap-y-14 md:grid-cols-2">
            {WHY.map((w) => {
              const Icon = w.icon;
              return (
                <Reveal key={w.title} className="flex gap-6 border-t border-slate-200 pt-8">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{w.title}</h3>
                    <p className="mt-3 max-w-md font-body text-lg leading-relaxed text-slate-600">{w.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5 — HOW ENGAGEMENT WORKS */}
      <section data-testid="fcio-process" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">The engagement approach</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["A clear path,", "from objective", "to outcome."]} />
            </h2>
          </div>
          <div className="mt-16 max-w-3xl">
            {PROCESS.map((s, i) => (
              <Reveal key={s.title} className="relative flex gap-6 pb-10 last:pb-0">
                {i < PROCESS.length - 1 && <span aria-hidden className="absolute left-6 top-14 h-[calc(100%-2.5rem)] w-px bg-slate-200" />}
                <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-navy font-heading text-lg font-bold text-white">{i + 1}</div>
                <div className="pt-1.5">
                  <h3 className="font-heading text-xl font-bold tracking-tight text-navy">{s.title}</h3>
                  <p className="mt-2 font-body leading-relaxed text-slate-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — BUSINESS OUTCOMES */}
      <section data-testid="fcio-outcomes" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">Business outcomes</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight md:text-5xl">
              <KineticHeadingInView lines={["The value the", "board can see."]} />
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/10 md:grid-cols-4">
            {OUTCOMES.map((o) => (
              <div key={o.title} className="bg-navy/80 px-6 py-8 backdrop-blur">
                <div className="font-heading text-lg font-semibold leading-snug tracking-tight text-white">{o.title}</div>
                <p className="mt-2 font-body text-sm leading-relaxed text-slate-400">{o.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHO BENEFITS */}
      <section data-testid="fcio-benefits" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Who benefits most</p>
              <h2 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
                <KineticHeadingInView lines={["Built for", "businesses at an", "inflection point."]} />
              </h2>
              <p className="mt-8 max-w-md font-body text-lg leading-relaxed text-slate-600">
                The value is greatest where the stakes are rising and the technology decisions are
                getting harder, across sectors and ownership structures alike.
              </p>
            </div>
            <div className="grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:col-span-7">
              {BENEFITS.map((name) => (
                <div key={name} data-testid={`fcio-benefit-${name.toLowerCase().replace(/\s|&/g, "-")}`} className="flex items-center bg-white p-6 transition-colors hover:bg-surface">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full bg-royal" />
                  <h3 className="ml-4 font-heading text-lg font-bold text-navy">{name}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — FAQ */}
      <section data-testid="fcio-faq" className="bg-surface py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Questions leaders ask</p>
            <h2 className="mt-6 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Straight answers,", "before we talk."]} />
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-14 max-w-4xl border-t border-slate-200">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`fcio-faq-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="py-6 font-heading text-xl font-bold text-navy hover:no-underline data-[state=open]:text-royal">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="max-w-3xl font-body text-lg leading-relaxed text-slate-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 9 — CLOSING */}
      <section data-testid="fcio-closing" className="relative overflow-hidden bg-navy py-24 text-white md:py-32 grain-overlay">
        <div className="pointer-events-none absolute -right-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">A closing thought</p>
          <blockquote className="mt-8 font-heading text-3xl font-light leading-[1.3] tracking-tight md:text-4xl">
            Technology decisions should support business growth, not create unnecessary complexity. A
            Fractional CIO provides experienced executive leadership, helping organisations make
            confident technology decisions without the long-term commitment of a full-time executive.
          </blockquote>
        </div>
      </section>

      <CTASection
        overline="Ready for executive technology leadership?"
        heading={["Let's discuss your", "business objectives."]}
        body="Whether your business is growing, expanding into new markets, evaluating AI opportunities or modernising enterprise technology, let's discuss how a Fractional CIO engagement can support your business objectives."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Explore Advisory Services", to: "/services/executive-advisory" }}
      />
    </>
  );
}
